import React, { useEffect, useState } from "react";
import { fetchRegions, fetchDiseaseRisk } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import DualExplainer from "@/components/DualExplainer";
import { Warning, CheckCircle, Fire, CircleNotch, Virus, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = {
  0: { label: "Niedrig",  color: "#00FA9A", bg: "rgba(0,250,154,0.12)", Icon: CheckCircle },
  1: { label: "Mäßig",    color: "#6B5C2D", bg: "#16140D", Icon: Warning },
  2: { label: "Erhöht",   color: "#FFC24B", bg: "rgba(255,215,0,0.10)", Icon: Warning },
  3: { label: "Kritisch", color: "#FF5500", bg: "rgba(255,85,0,0.12)", Icon: Fire },
};

export default function DiseaseWatch() {
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetchRegions().then((r) => { setRegions(r); if (r[0]) setRegionId(r[0].id); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!regionId) return;
    setItems(null);
    fetchDiseaseRisk(regionId)
      .then((d) => setItems(d.items || []))
      .catch(() => toast.error("Analyse konnte nicht geladen werden"));
  }, [regionId]);

  return (
    <div className="space-y-6" data-testid="diseases-root">
      <header>
        <div className="eid-label flex items-center gap-1.5"><Virus size={11} /> Pflanzenschutz · Wetter-basierte Prognose</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Krankheits-Frühwarner</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Aus 7-Tage-Wetter + Bodenfeuchte schätzen wir den Infektionsdruck für
          die vier häufigsten Pilzkrankheiten im Getreide- und Kartoffelbau.
        </p>
      </header>

      <DualExplainer
        title="Warum Wetter den Pilzdruck bestimmt"
        testid="diseases-explainer"
        simple={
          <>
            <p>
              Die meisten Getreide- und Kartoffelkrankheiten sind <strong>Pilze</strong>.
              Pilze brauchen <strong>Feuchte</strong> (Bestandsnässe, feuchter Boden) und die
              <strong> richtige Temperatur</strong>, um sich auszubreiten.
            </p>
            <p>
              Wenn unsere Wetterdaten genau diese Bedingungen zeigen, bekommst du hier
              eine Warnung — oft <strong>Tage bevor</strong> du den Befall draußen siehst.
              So kannst du gezielt behandeln statt im Blindflug zu spritzen.
            </p>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Kombiniere die Warnung mit einer{" "}
              <strong>Kontrollfahrt</strong>. Die App ist kein Ersatz für den Blick aufs Feld.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Methodik:</strong> Regelbasiertes Risiko-Scoring (nicht modellbasiert)
              aus 7-Tage Mittelwerten: T_mean (°C), T_diurnal (max-min), VWC 0–10 cm (m³ m⁻³).
              Pro Krankheit 2–3 boolean Regeln → Score 0–3.
            </p>
            <p>
              <strong>Limitationen:</strong> Für produktive Empfehlungen sind etablierte
              Modelle wie SIMCERC (Septoria), Negfry / Simphyt (Phytophthora), oder SimLEP
              (DLR RLP) deutlich präziser — sie arbeiten mit Blattnässedauer-Sensorik und
              stündlichen Feinwetterwerten. Unsere Heuristik ersetzt keine Beratung durch
              den offiziellen Pflanzenschutzwarndienst (DWD / ZAMG / LfL / AGES).
            </p>
          </>
        }
      />

      <div className="max-w-md" data-testid="diseases-region-wrap">
        <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="diseases-region-select" />
      </div>

      {!items && (
        <div className="eid-card p-8 flex items-center justify-center gap-2 text-zinc-400" data-testid="diseases-loading">
          <CircleNotch size={18} className="animate-spin" /> Analysiere Wetter &amp; Feuchte…
        </div>
      )}

      {items && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="diseases-grid">
          {items.map((d) => <DiseaseCard key={d.id} item={d} />)}
        </section>
      )}

      <div className="text-xs text-zinc-400 border-l-2 border-[#FFD700]/40 pl-3 leading-relaxed">
        Grobe Orientierungswerte aus Wettermodellen — ersetzen keinen Pflanzenschutz-
        warndienst und keine Beratung durch Landwirtschaftskammer, DLR, LfL oder AGES.
        Pflanzenschutzmittel nur nach gültiger Zulassung und Indikation einsetzen.
      </div>
    </div>
  );
}

function DiseaseCard({ item }) {
  const meta = TONE[item.score ?? 0];
  const Icon = meta.Icon;
  return (
    <article className="eid-card overflow-hidden" data-testid={`disease-card-${item.id}`}>
      <div className="p-3 flex items-start gap-3" style={{ background: meta.bg }}>
        <span className="shrink-0 p-2 rounded-sm bg-black/60" style={{ color: meta.color }}>
          <Icon size={20} weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
              Stufe {item.score}/3 · {meta.label}
            </div>
          </div>
          <h3 className="text-base font-medium mt-0.5 leading-tight">{item.label}</h3>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-xs text-zinc-400 italic">{item.explain}</p>
        {item.reasons?.length > 0 ? (
          <ul className="text-xs text-white space-y-1 pl-4 list-disc">
            {item.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        ) : (
          <div className="text-xs text-zinc-500">Keine Risikofaktoren aktuell erkennbar.</div>
        )}
        <div className="flex items-start gap-1.5 border-t border-white/10 pt-2">
          <Shield size={12} className="shrink-0 mt-0.5 text-[#00FA9A]" />
          <p className="text-xs text-white"><strong>Tipp:</strong> {item.tip}</p>
        </div>
      </div>
    </article>
  );
}
